import * as React from 'react';
import { useEffect, useRef } from 'react';

/** Material UI Packages. */
import { Box, TextField, Typography } from '@mui/material';

/** Internal Components. */
import { SelectableOptionRow } from 'shared/ui/FilterOptionLists';
import { geocodedLocationString, placeOptionParts, useGooglePlacesAutocomplete } from 'shared/lib/handleGoogle';

/**
 * Lets the operator find a delivery address with Google suggestions; the picked address stays
 * on top and clicking it clears it. A picked place is looked up before it is set, and the
 * parent hears whether the lookup is running or failed so it can hold APPLY. An answer that
 * comes after the popover closed, or after another place was picked, is ignored.
 *
 * @param {string} value - Committed address ("" when none).
 * @param {function} onChange - Called with the geocoded "CITY, STATE, COUNTRY" string, or "".
 * @param {function} [onLookupChange] - Called with 'pending', 'failed' or null.
 */
export default function DeliveryLocationPicker({ value, onChange, onLookupChange = () => {} }) {
    const { options, inputValue, handleInputChange, endSession, ready } = useGooglePlacesAutocomplete({
        value,
        seedValue: false,
    });
    const lookupSeq = useRef(0);
    const reportLookup = useRef(onLookupChange);
    reportLookup.current = onLookupChange;

    // Closing the popover unmounts the picker: forget any lookup still in flight.
    useEffect(
        () => () => {
            lookupSeq.current += 1;
            reportLookup.current(null);
        },
        []
    );

    // The draft changed from outside (RESET): a pick still in flight or failed no longer applies.
    useEffect(() => {
        lookupSeq.current += 1;
        reportLookup.current(null);
    }, [value]);

    const commit = (next) => {
        lookupSeq.current += 1;
        onLookupChange(null);
        onChange(next);
    };

    const pick = (prediction) => {
        endSession();
        if (!prediction?.place_id || !window.google) return commit('');
        const seq = ++lookupSeq.current;
        onLookupChange('pending');
        const geocoder = new window.google.maps.Geocoder();
        return geocoder
            .geocode({ placeId: prediction.place_id })
            .then(({ results }) => {
                if (seq !== lookupSeq.current) return;
                if (!results?.[0]) return onLookupChange('failed');
                onLookupChange(null);
                onChange(geocodedLocationString(results[0]));
            })
            .catch((e) => {
                console.error('Geocoding error:', e.message);
                if (seq === lookupSeq.current) onLookupChange('failed');
            });
    };

    const typeAddress = (event) => {
        // A new search drops the failed (or pending) pick.
        lookupSeq.current += 1;
        onLookupChange(null);
        handleInputChange(event, event.target.value, 'input');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <TextField
                size="small"
                autoFocus
                value={inputValue}
                onChange={typeAddress}
                placeholder={ready ? 'Search address' : 'Loading Google Places…'}
                inputProps={{ 'aria-label': 'Search address' }}
                sx={{ '& .MuiInputBase-input': { fontSize: '12px', py: 0.5 } }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto' }}>
                {value ? <SelectableOptionRow primary={value} selected onClick={() => commit('')} /> : null}
                {options.map((option) => {
                    const { mainText, secondaryText } = placeOptionParts(option);
                    return (
                        <SelectableOptionRow
                            key={option.place_id || mainText}
                            primary={mainText}
                            secondary={secondaryText}
                            selected={false}
                            onClick={() => pick(option)}
                        />
                    );
                })}
                {!value && options.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>
                        {inputValue ? 'No locations' : 'Type to search'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
